import { tC } from "../../utils/TranslateWithContentLanguage";
import {
  Layers,
  RefreshCw,
  Brain,
  BookOpen,
  Hammer,
  Microscope,
  Scale,
  Lightbulb,
  BarChart,
  BookmarkIcon,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CardImage from "../tool_components/CardImage";
import { Button } from "@core/button";
import Text from "@core/text";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { BloomsTaxonomyActivitiesResponseDto } from "@packages/api/madlenAPI.schemas";
import BloomTaxonomyCardPDF from "./BloomTaxonomyCardPdf";
import { MascotBubble } from "../tool_components/MascotBubble";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import { useIsAuthor } from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";
import {
  getGetBloomsTaxonomyActivitiesQueryKey,
  getGetUserBloomsTaxonomyActivitiesQueryKey,
  updateBloomsTaxonomyActivities,
} from "@packages/api/madlenAPI";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";

type TabType =
  | "overview"
  | "REMEMBER"
  | "UNDERSTAND"
  | "APPLY"
  | "ANALYZE"
  | "EVALUATE"
  | "CREATE";

// Define taxonomy level details with better styling
const taxonomyLevels = [
  {
    id: "REMEMBER" as TabType,
    name: "bloomsTaxonomyActivity.remember.label",
    color: "#8A80E0", // Purple color
    bgColor: "bg-[#8A80E0]",
    hoverBgColor: "hover:bg-[#7B70D0]",
    darkBgColor: "dark:bg-[#6758C9]",
    gradient: "from-[#8A80E0] to-[#7B70D0]",
    darkGradient: "dark:from-[#6758C9] dark:to-[#5748B9]",
  },
  {
    id: "UNDERSTAND" as TabType,
    name: "bloomsTaxonomyActivity.understand.label",
    color: "#73B6E6", // Blue color
    bgColor: "bg-[#73B6E6]",
    hoverBgColor: "hover:bg-[#62A5D5]",
    darkBgColor: "dark:bg-[#5A8EC0]",
    gradient: "from-[#73B6E6] to-[#62A5D5]",
    darkGradient: "dark:from-[#5A8EC0] dark:to-[#4A7DB0]",
  },
  {
    id: "APPLY" as TabType,
    name: "bloomsTaxonomyActivity.apply.label",
    color: "#50C0B4", // Teal color
    bgColor: "bg-[#50C0B4]",
    hoverBgColor: "hover:bg-[#40B0A4]",
    darkBgColor: "dark:bg-[#3D8AD6]",
    gradient: "from-[#50C0B4] to-[#40B0A4]",
    darkGradient: "dark:from-[#3D8AD6] dark:to-[#3278C1]",
  },
  {
    id: "ANALYZE" as TabType,
    name: "bloomsTaxonomyActivity.analyze.label",
    color: "#8BC34A", // Light green color
    bgColor: "bg-[#8BC34A]",
    hoverBgColor: "hover:bg-[#7BB33A]",
    darkBgColor: "dark:bg-[#7DAF5E]",
    gradient: "from-[#8BC34A] to-[#7BB33A]",
    darkGradient: "dark:from-[#7DAF5E] dark:to-[#6FA14F]",
  },
  {
    id: "EVALUATE" as TabType,
    name: "bloomsTaxonomyActivity.evaluate.label",
    color: "#FFC107", // Yellow color
    bgColor: "bg-[#FFC107]",
    hoverBgColor: "hover:bg-[#FFB300]",
    darkBgColor: "dark:bg-[#E09E3E]",
    gradient: "from-[#FFC107] to-[#FFB300]",
    darkGradient: "dark:from-[#E09E3E] dark:to-[#D19132]",
  },
  {
    id: "CREATE" as TabType,
    name: "bloomsTaxonomyActivity.create.label",
    color: "#FF5722", // Red/orange color
    bgColor: "bg-[#FF5722]",
    hoverBgColor: "hover:bg-[#F4511E]",
    darkBgColor: "dark:bg-[#E05252]",
    gradient: "from-[#FF5722] to-[#F4511E]",
    darkGradient: "dark:from-[#E05252] dark:to-[#D14141]",
  },
] as const;

interface BloomTaxonomyCardProps {
  bloomsTaxonomyActivity: BloomsTaxonomyActivitiesResponseDto;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
}

export const BloomTaxonomyCard = ({
  bloomsTaxonomyActivity,
  isImageLoading,
  onCreateAgain,
  isEditable = false,
}: BloomTaxonomyCardProps) => {
  const finalImageUrl = useImagePdf(bloomsTaxonomyActivity.imageUrl);
  const contentLanguage = bloomsTaxonomyActivity.contentLanguage || "tr";
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const scrollRef = useRef<HTMLDivElement>(null);
  const pyramidRef = useRef<HTMLDivElement>(null);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const isAuthor = useIsAuthor(bloomsTaxonomyActivity.authorId);

  const queryClient = useQueryClient();

  // Reset animation state when tab changes to overview
  useEffect(() => {
    if (activeTab === "overview") {
      setTimeout(() => {
        if (pyramidRef.current) {
          pyramidRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100);
    }
  }, [activeTab]);

  const tabs = [
    {
      id: "overview",
      icon: BarChart,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.overview"),
    },
    {
      id: "REMEMBER",
      icon: Brain,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.remember.label"),
    },
    {
      id: "UNDERSTAND",
      icon: BookOpen,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.understand.label"),
    },
    {
      id: "APPLY",
      icon: Hammer,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.apply.label"),
    },
    {
      id: "ANALYZE",
      icon: Microscope,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.analyze.label"),
    },
    {
      id: "EVALUATE",
      icon: Scale,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.evaluate.label"),
    },
    {
      id: "CREATE",
      icon: Lightbulb,
      label: tC(contentLanguage, "bloomsTaxonomyActivity.create.label"),
    },
  ];

  const renderTabContent = () => {
    if (activeTab === "overview") {
      const levelActivities = taxonomyLevels.map((level) => {
        const activity = bloomsTaxonomyActivity.activities?.find(
          (activity) => activity.level?.toUpperCase() === level.id,
        );

        return {
          ...level,
          shortDescription:
            activity?.description?.split(".")[0] + "." ||
            activity?.activity_title ||
            "",
          activity,
        };
      });

      return (
        <div className="flex flex-col items-center py-6">
          {/* Title and description section */}
          <div className="w-full max-w-2xl mx-auto text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              {tC(contentLanguage, "bloomsTaxonomyActivity.taxonomyTitle")}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-4">
              <EditableMarkdown
                value={bloomsTaxonomyActivity.description || ""}
                isEditable={isEditable ?? true}
                handleSave={async (newValue) => {
                  await updateBloomsTaxonomyActivities(
                    bloomsTaxonomyActivity.id,
                    {
                      description: newValue,
                    },
                  );

                  await queryClient.invalidateQueries({
                    queryKey: getGetUserBloomsTaxonomyActivitiesQueryKey(),
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetBloomsTaxonomyActivitiesQueryKey(
                      bloomsTaxonomyActivity.id,
                    ),
                  });
                }}
              />
            </p>

            {/* Citation */}
            <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-4">
              Zaphir et al. (2024), Stritto et al. (2024) [Oregon State
              University], Montclair University (2024).
            </p>
          </div>

          {/* Enhanced pyramid visualization with smooth animations */}
          <div className="w-full max-w-4xl mx-auto px-4 my-8 relative">
            {/* Instructions above the pyramid */}
            <div className="text-center italic text-sm text-gray-500 dark:text-gray-400 mb-6">
              {tC(
                contentLanguage,
                "bloomsTaxonomyActivity.triangleInteractionHint",
              )}
            </div>

            <div className="flex flex-col items-center space-y-1.5">
              {/* The pyramid levels - normal pyramid with Remember at the bottom */}
              <AnimatePresence>
                {[...levelActivities].reverse().map((level, index) => {
                  // Calculate width for proper pyramid shape - widest at bottom (Remember)
                  const width = `${30 + index * 14}%`;

                  return (
                    <motion.div
                      key={level.id}
                      className={`pyramid-level relative w-full mx-auto ${index < levelActivities.length - 1 ? "mb-1.5" : ""}`}
                      style={{
                        width: width,
                      }}
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.15,
                        ease: "easeOut",
                      }}
                    >
                      <motion.div
                        onClick={() => {
                          setActiveTab(level.id as TabType);
                          scrollRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }}
                        className={`group w-full py-3 sm:py-4 text-white font-semibold bg-gradient-to-r ${level.gradient} ${level.darkGradient} rounded-md shadow-lg hover:shadow-xl relative overflow-hidden transform hover:scale-[1.01] hover:-translate-y-0.5 cursor-pointer transition-all duration-300`}
                        whileHover={{ scale: 1.02, y: -4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {/* Replace tooltip with animated activity title */}
                        <div className="px-3 flex flex-col items-center justify-center">
                          <span className="group-hover:scale-105 transition-transform duration-300">
                            {tC(contentLanguage, level.name)}
                          </span>

                          {/* Animated activity title */}
                          {level.activity?.activity_title && (
                            <div className="w-full overflow-hidden h-0 group-hover:h-6 transition-all duration-300 ease-in-out">
                              <p className="mx-auto text-center w-full text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150 mx-auto truncate">
                                {level.activity.activity_title}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
            <div style={{ height: "30px" }} ref={pyramidRef} />
          </div>

          {/* Ethical Usage Tips */}
          <div className="w-full max-w-4xl mx-auto mt-4 mb-2">
            <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg border border-blue-200/20 p-4">
              <div className="flex items-center gap-2 mb-3 text-blue-500">
                <BookmarkIcon size={18} />
                <h4 className="text-sm font-medium text-blue-500">
                  {tC(
                    contentLanguage,
                    "bloomsTaxonomyActivity.ethicalUsageTips",
                  )}
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <div className="pt-1 text-blue-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {tC(contentLanguage, "bloomsTaxonomyActivity.ethicTip1")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="pt-1 text-blue-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {tC(contentLanguage, "bloomsTaxonomyActivity.ethicTip2")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="pt-1 text-blue-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {tC(contentLanguage, "bloomsTaxonomyActivity.ethicTip3")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="pt-1 text-blue-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {tC(contentLanguage, "bloomsTaxonomyActivity.ethicTip4")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Filter activities by the active tab (taxonomy level)
    const filteredActivities = bloomsTaxonomyActivity.activities?.filter(
      (activity) => {
        if (!activity.level) return false;
        return activity.level === activeTab;
      },
    );

    // Handle taxonomy level tabs
    const taxonomyLevel = taxonomyLevels.find(
      (level) => level.id === activeTab,
    );

    if (taxonomyLevel) {
      if (!filteredActivities || filteredActivities.length === 0) {
        return (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {tC(contentLanguage, "bloomsTaxonomyActivity.noActivitiesForLevel")}
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <h3
            className="font-semibold mb-4"
            style={{
              color: taxonomyLevel.color || "#81C784",
            }}
          >
            {tC(contentLanguage, "bloomsTaxonomyActivity.levelActivities", {
              level: tC(
                contentLanguage,
                `bloomsTaxonomyActivity.${activeTab.toLowerCase()}.label`,
              ),
            })}
          </h3>
          <div className="grid grid-cols-1 gap-6">
            {filteredActivities.map((activity, index: number) => {
              const taxonomyLevel = taxonomyLevels.find(
                (level) => level.id === activity.level,
              );

              const levelColor = taxonomyLevel?.color || "#F3C969";

              return (
                <div
                  key={index}
                  className="rounded-lg p-5 border hover:shadow-2xl shadow-lg transition-all duration-300"
                  style={{
                    backgroundColor: `${levelColor}10`,
                    borderColor: `${levelColor}30`,
                    boxShadow: `0 4px 6px -1px ${levelColor}10, 0 2px 4px -1px ${levelColor}10`,
                  }}
                >
                  {activity.level && (
                    <div className="mb-2">
                      <span
                        className="inline-block px-3 py-1 text-sm font-medium rounded-full text-white"
                        style={{ backgroundColor: levelColor }}
                      >
                        {tC(
                          contentLanguage,
                          `bloomsTaxonomyActivity.${activity.level.toLowerCase()}.label`,
                        )}
                      </span>
                    </div>
                  )}

                  {activity.activity_title && (
                    <h4
                      className="text-lg font-medium mb-3"
                      style={{ color: levelColor }}
                    >
                      <EditableMarkdown
                        value={activity.activity_title}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!bloomsTaxonomyActivity.activities) return;

                          const activityIndex =
                            bloomsTaxonomyActivity.activities.findIndex(
                              (a) =>
                                a.level === activity.level &&
                                a.activity_title === activity.activity_title,
                            );

                          if (activityIndex === -1) return;

                          const updatedActivities = [
                            ...bloomsTaxonomyActivity.activities,
                          ];
                          updatedActivities[activityIndex] = {
                            ...updatedActivities[activityIndex],
                            activity_title: newValue,
                          };

                          await updateBloomsTaxonomyActivities(
                            bloomsTaxonomyActivity.id,
                            {
                              activities: updatedActivities,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserBloomsTaxonomyActivitiesQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetBloomsTaxonomyActivitiesQueryKey(
                              bloomsTaxonomyActivity.id,
                            ),
                          });
                        }}
                      />
                    </h4>
                  )}

                  {activity.description && (
                    <div className="mb-4">
                      <EditableMarkdown
                        value={activity.description}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!bloomsTaxonomyActivity.activities) return;

                          const activityIndex =
                            bloomsTaxonomyActivity.activities.findIndex(
                              (a) =>
                                a.level === activity.level &&
                                a.activity_title === activity.activity_title,
                            );

                          if (activityIndex === -1) return;

                          const updatedActivities = [
                            ...bloomsTaxonomyActivity.activities,
                          ];
                          updatedActivities[activityIndex] = {
                            ...updatedActivities[activityIndex],
                            description: newValue,
                          };

                          await updateBloomsTaxonomyActivities(
                            bloomsTaxonomyActivity.id,
                            {
                              activities: updatedActivities,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserBloomsTaxonomyActivitiesQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetBloomsTaxonomyActivitiesQueryKey(
                              bloomsTaxonomyActivity.id,
                            ),
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* Instructions */}
                  {activity.instructions &&
                    activity.instructions.length > 0 && (
                      <div className="mb-4">
                        <h5
                          className="font-medium mb-2"
                          style={{ color: levelColor }}
                        >
                          {tC(
                            contentLanguage,
                            "bloomsTaxonomyActivity.instructions",
                          )}
                          :
                        </h5>
                        <ul className="list-disc pl-5 space-y-1">
                          {activity.instructions.map(
                            (instruction: string, instructionIdx: number) => (
                              <li
                                key={instructionIdx}
                                className="leading-relaxed"
                              >
                                <EditableMarkdown
                                  value={instruction}
                                  isEditable={isEditable ?? true}
                                  handleSave={async (newValue) => {
                                    if (!bloomsTaxonomyActivity.activities)
                                      return;

                                    const activityIndex =
                                      bloomsTaxonomyActivity.activities.findIndex(
                                        (a) =>
                                          a.level === activity.level &&
                                          a.activity_title ===
                                            activity.activity_title,
                                      );

                                    if (activityIndex === -1) return;
                                    if (
                                      !bloomsTaxonomyActivity.activities[
                                        activityIndex
                                      ].instructions
                                    )
                                      return;

                                    const updatedInstructions = [
                                      ...bloomsTaxonomyActivity.activities[
                                        activityIndex
                                      ].instructions,
                                    ];
                                    updatedInstructions[instructionIdx] =
                                      newValue;

                                    const updatedActivities = [
                                      ...bloomsTaxonomyActivity.activities,
                                    ];
                                    updatedActivities[activityIndex] = {
                                      ...updatedActivities[activityIndex],
                                      instructions: updatedInstructions,
                                    };

                                    await updateBloomsTaxonomyActivities(
                                      bloomsTaxonomyActivity.id,
                                      {
                                        activities: updatedActivities,
                                      },
                                    );

                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetUserBloomsTaxonomyActivitiesQueryKey(),
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetBloomsTaxonomyActivitiesQueryKey(
                                          bloomsTaxonomyActivity.id,
                                        ),
                                    });
                                  }}
                                />
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Deliverables */}
                  {activity.deliverables && (
                    <div className="mb-4">
                      <h5
                        className="font-medium mb-2"
                        style={{ color: levelColor }}
                      >
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.deliverables",
                        )}
                        :
                      </h5>
                      <EditableMarkdown
                        value={activity.deliverables}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!bloomsTaxonomyActivity.activities) return;

                          const activityIndex =
                            bloomsTaxonomyActivity.activities.findIndex(
                              (a) =>
                                a.level === activity.level &&
                                a.activity_title === activity.activity_title,
                            );

                          if (activityIndex === -1) return;

                          const updatedActivities = [
                            ...bloomsTaxonomyActivity.activities,
                          ];
                          updatedActivities[activityIndex] = {
                            ...updatedActivities[activityIndex],
                            deliverables: newValue,
                          };

                          await updateBloomsTaxonomyActivities(
                            bloomsTaxonomyActivity.id,
                            {
                              activities: updatedActivities,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserBloomsTaxonomyActivitiesQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetBloomsTaxonomyActivitiesQueryKey(
                              bloomsTaxonomyActivity.id,
                            ),
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* Assessment Criteria */}
                  {activity.assessment_criteria &&
                    activity.assessment_criteria.length > 0 && (
                      <div className="mb-4">
                        <h5
                          className="font-medium mb-2"
                          style={{ color: levelColor }}
                        >
                          {tC(
                            contentLanguage,
                            "bloomsTaxonomyActivity.assessmentCriteria",
                          )}
                          :
                        </h5>
                        <ul className="list-disc pl-5 space-y-1">
                          {activity.assessment_criteria.map(
                            (criterion: string, criterionIdx: number) => (
                              <li
                                key={criterionIdx}
                                className="leading-relaxed"
                              >
                                <EditableMarkdown
                                  value={criterion}
                                  isEditable={isEditable ?? true}
                                  handleSave={async (newValue) => {
                                    if (!bloomsTaxonomyActivity.activities)
                                      return;

                                    const activityIndex =
                                      bloomsTaxonomyActivity.activities.findIndex(
                                        (a) =>
                                          a.level === activity.level &&
                                          a.activity_title ===
                                            activity.activity_title,
                                      );

                                    if (activityIndex === -1) return;
                                    if (
                                      !bloomsTaxonomyActivity.activities[
                                        activityIndex
                                      ].assessment_criteria
                                    )
                                      return;

                                    const updatedCriteria = [
                                      ...bloomsTaxonomyActivity.activities[
                                        activityIndex
                                      ].assessment_criteria,
                                    ];
                                    updatedCriteria[criterionIdx] = newValue;

                                    const updatedActivities = [
                                      ...bloomsTaxonomyActivity.activities,
                                    ];
                                    updatedActivities[activityIndex] = {
                                      ...updatedActivities[activityIndex],
                                      assessment_criteria: updatedCriteria,
                                    };

                                    await updateBloomsTaxonomyActivities(
                                      bloomsTaxonomyActivity.id,
                                      {
                                        activities: updatedActivities,
                                      },
                                    );

                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetUserBloomsTaxonomyActivitiesQueryKey(),
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey:
                                        getGetBloomsTaxonomyActivitiesQueryKey(
                                          bloomsTaxonomyActivity.id,
                                        ),
                                    });
                                  }}
                                />
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {/* AI Integration */}
                  {activity.ai_integration && (
                    <div>
                      <h5
                        className="font-medium mb-2"
                        style={{ color: levelColor }}
                      >
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.aiIntegration",
                        )}
                        :
                      </h5>
                      <EditableMarkdown
                        value={activity.ai_integration}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!bloomsTaxonomyActivity.activities) return;

                          const activityIndex =
                            bloomsTaxonomyActivity.activities.findIndex(
                              (a) =>
                                a.level === activity.level &&
                                a.activity_title === activity.activity_title,
                            );

                          if (activityIndex === -1) return;

                          const updatedActivities = [
                            ...bloomsTaxonomyActivity.activities,
                          ];
                          updatedActivities[activityIndex] = {
                            ...updatedActivities[activityIndex],
                            ai_integration: newValue,
                          };

                          await updateBloomsTaxonomyActivities(
                            bloomsTaxonomyActivity.id,
                            {
                              activities: updatedActivities,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserBloomsTaxonomyActivitiesQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetBloomsTaxonomyActivitiesQueryKey(
                              bloomsTaxonomyActivity.id,
                            ),
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      data-testid="bloom-taxonomy-card"
      className="min-h-screen bg-gradient-to-b from-[#FFF7E6]/20 to-white dark:from-[#2E4223]/20 dark:to-gray-900"
    >
      {isAuthor && (
        <MascotBubble
          createChatParams={{
            teacherToolId: bloomsTaxonomyActivity.id,
            toolType: "BLOOMS_TAXONOMY_ACTIVITIES",
          }}
          toolQueryKey={getGetBloomsTaxonomyActivitiesQueryKey(
            bloomsTaxonomyActivity.id,
          )}
          toolsQueryKey={getGetUserBloomsTaxonomyActivitiesQueryKey()}
        />
      )}
      <div className="container max-w-7xl mx-auto px-4 py-4">
        <CardImage
          createdAt={bloomsTaxonomyActivity.createdAt}
          imageUrl={bloomsTaxonomyActivity.imageUrl}
          videoUrl={bloomsTaxonomyActivity.videoUrl}
          courseName={bloomsTaxonomyActivity.courseName}
          gradeLevel={Number(bloomsTaxonomyActivity.gradeLevel)}
          theme="bloom"
          isLoading={isImageLoading}
          topic={
            <div className="flex items-center gap-4 justify-center">
              <div className="p-3 rounded-xl bg-[#FFF7E6] dark:bg-[#2E4223]/30 text-bloomsTaxonomy dark:text-bloomsTaxonomyDark">
                <Layers size={24} />
              </div>
              <div>
                <Text variant={"h1"} className="mb-2">
                  {bloomsTaxonomyActivity.title}
                </Text>
              </div>
            </div>
          }
          contentLanguage={contentLanguage}
        />

        <div className="flex justify-end gap-2 mt-2 mb-6">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-[#F3C969] to-[#81C784] hover:from-[#FFD54F] hover:to-[#66BB6A]"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            document={
              <BloomTaxonomyCardPDF
                customizationSettings={
                  customizationSettings as PdfCustomizationSettings | undefined
                }
                bloomsTaxonomyActivity={{
                  ...bloomsTaxonomyActivity,
                  imageUrl: finalImageUrl,
                }}
              />
            }
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            pdfTitle={bloomsTaxonomyActivity.title || ""}
            btnTheme="bg-gradient-to-r from-[#F3C969] to-[#81C784]"
          />
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 shadow-[#F3C969]/5">
          {/* Tab navigation */}
          <div className="flex sticky -top-6 rounded-2xl z-10 flex-col gap-2 p-4 bg-[#FFF7E6] dark:bg-[#2E4223]">
            {/* Top row: Overview tab only */}
            <div className="flex justify-center w-full mb-2">
              <button
                key="overview"
                onClick={() => {
                  setActiveTab("overview");
                  if (pyramidRef.current) {
                    pyramidRef.current.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                    });
                  }
                }}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300
                ${
                  activeTab === "overview"
                    ? "bg-gradient-to-r from-[#F3C969] to-[#81C784] text-white shadow-lg"
                    : "bg-white/50 dark:bg-gray-800/50 hover:bg-[#F3C969]/10 text-gray-700 dark:text-gray-200 hover:shadow-md shadow-sm"
                }`}
              >
                <BarChart size={18} />
                <span className="md:inline">
                  {tC(contentLanguage, "bloomsTaxonomyActivity.overview")}
                </span>
              </button>
            </div>

            {/* Bottom row: Taxonomy level tabs */}
            <div className="flex flex-wrap justify-center gap-2 w-full">
              {taxonomyLevels.map((level) => {
                const tab = tabs.find((tab) => tab.id === level.id);
                if (!tab) return null;

                const Icon = tab.icon;
                const isActive = activeTab === level.id;
                const levelColor = level.color;
                const bgGradient = `bg-gradient-to-r ${level.gradient} ${level.darkGradient}`;

                return (
                  <button
                    key={level.id}
                    onClick={() => {
                      setActiveTab(level.id);
                      scrollRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
                    ${
                      isActive
                        ? bgGradient + " text-white shadow-lg"
                        : `bg-white/50 dark:bg-gray-800/50 hover:bg-opacity-10 hover:bg-[${levelColor}] text-[${levelColor}] dark:text-[${levelColor}] hover:shadow-md shadow-sm`
                    }`}
                  >
                    <Icon size={18} />
                    <span className="hidden md:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6 relative">
            <div ref={scrollRef} className="absolute -top-20 -z-10" />
            {renderTabContent()}
            {/* Regenerating overlay */}
            {bloomsTaxonomyActivity.status === "REGENERATING" && (
              <RegeneratingOverlay />
            )}
          </div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <BloomTaxonomyCardPDF
            customizationSettings={
              customizationSettings as PdfCustomizationSettings | undefined
            }
            bloomsTaxonomyActivity={{
              ...bloomsTaxonomyActivity,
              imageUrl: finalImageUrl,
            }}
          />
        }
      />
    </div>
  );
};

export default BloomTaxonomyCard;
